import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Family } from '../../types';
import { familyService } from '../../services/familyService';

interface FamilySelectorProps {
  categoryId: string;
  selectedFamilyId: string;
  onFamilyChange: (familyId: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const SelectorContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SelectorInput = styled.input<{ disabled?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ecf0f1;
  border-radius: 8px;
  font-size: 1rem;
  background-color: ${props => props.disabled ? '#f8f9fa' : 'white'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.disabled ? '#ecf0f1' : 'var(--primary-orange)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 3px rgba(255, 140, 66, 0.1)'};
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
`;

const DropdownItem = styled.div<{ selected?: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  background-color: ${props => props.selected ? '#fff3e0' : 'white'};
  color: ${props => props.selected ? '#e65100' : '#333'};
  font-weight: ${props => props.selected ? '600' : 'normal'};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.selected ? '#fff3e0' : '#f8f9fa'};
    color: ${props => props.selected ? '#e65100' : 'var(--primary-orange)'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const LoadingMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
`;

const NoResultsMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
`;

const HelpText = styled.small`
  color: #666;
  font-size: 0.8rem;
  margin-top: 4px;
  display: block;
`;

const FamilySelector: React.FC<FamilySelectorProps> = ({
  categoryId,
  selectedFamilyId,
  onFamilyChange,
  disabled = false,
  placeholder = "Buscar familia..."
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [families, setFamilies] = useState<Family[]>([]);
  const [filteredFamilies, setFilteredFamilies] = useState<Family[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const selectorRef = useRef<HTMLDivElement>(null);

  // Cargar familias cuando cambia la categoría
  useEffect(() => {
    if (categoryId && !disabled) {
      loadFamilies();
    } else {
      setFamilies([]);
      setFilteredFamilies([]);
      setSelectedFamily(null);
      setSearchTerm('');
    }
  }, [categoryId, disabled]);

  // Filtrar familias cuando cambia el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      // Sin búsqueda: mostrar solo las primeras 5 familias para evitar lentitud
      setFilteredFamilies(families.slice(0, 5));
    } else {
      // Con búsqueda: mostrar todos los resultados coincidentes
      const filtered = families.filter(family =>
        family.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredFamilies(filtered);
    }
  }, [searchTerm, families]);

  // Actualizar familia seleccionada cuando cambia el ID seleccionado
  useEffect(() => {
    if (selectedFamilyId && families.length > 0) {
      // Convertir ambos a string para comparación consistente
      const family = families.find(f => String(f.id) === String(selectedFamilyId));
      if (family) {
        setSelectedFamily(family);
        setSearchTerm(family.name);
        console.log('Familia seleccionada encontrada:', family.name);
      } else {
        console.log('Familia no encontrada para ID:', selectedFamilyId, 'en familias:', families.map(f => f.id));
      }
    } else {
      setSelectedFamily(null);
      setSearchTerm('');
    }
  }, [selectedFamilyId, families]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Si no hay familia seleccionada, limpiar el campo
        if (!selectedFamily) {
          setSearchTerm('');
        } else {
          setSearchTerm(selectedFamily.name);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedFamily]);

  const loadFamilies = async () => {
    setLoading(true);
    try {
      // Cargar todas las familias de la categoría para tener datos completos para búsqueda
      const response = await familyService.getFamiliesWithPagination(1, 100, categoryId);
      if (response && response.families) {
        setFamilies(response.families);
        // Inicialmente mostrar solo las primeras 5 familias
        setFilteredFamilies(response.families.slice(0, 5));
      }
    } catch (error) {
      console.error('Error al cargar familias:', error);
      setFamilies([]);
      setFilteredFamilies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    
    // Si el usuario borra el texto, limpiar la selección
    if (!value.trim()) {
      setSelectedFamily(null);
      onFamilyChange('');
    }
  };

  const handleFamilySelect = (family: Family) => {
    setSelectedFamily(family);
    setSearchTerm(family.name);
    setIsOpen(false);
    onFamilyChange(family.id);
  };

  const handleInputFocus = () => {
    if (!disabled && categoryId) {
      setIsOpen(true);
    }
  };

  const handleClearSelection = () => {
    setSelectedFamily(null);
    setSearchTerm('');
    onFamilyChange('');
    setIsOpen(false);
  };

  return (
    <SelectorContainer ref={selectorRef}>
      <SelectorInput
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={disabled ? "Primero selecciona una categoría" : placeholder}
        disabled={disabled || !categoryId}
      />
      
      {!categoryId && (
        <HelpText>Primero selecciona una categoría</HelpText>
      )}
      
      {isOpen && categoryId && !disabled && (
        <DropdownContainer>
          {loading ? (
            <LoadingMessage>Cargando familias...</LoadingMessage>
          ) : (
            <>
              {/* Opción para limpiar selección */}
              <DropdownItem
                onClick={handleClearSelection}
                selected={!selectedFamily}
              >
                <em>Sin familia</em>
              </DropdownItem>
              
              {filteredFamilies.length > 0 ? (
                filteredFamilies.map(family => (
                  <DropdownItem
                    key={family.id}
                    onClick={() => handleFamilySelect(family)}
                    selected={selectedFamily?.id === family.id}
                  >
                    {family.name}
                    {family.description && (
                      <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                        {family.description}
                      </div>
                    )}
                  </DropdownItem>
                ))
              ) : (
                <NoResultsMessage>
                  {searchTerm ? `No se encontraron familias que coincidan con "${searchTerm}"` : 'No hay familias disponibles'}
                </NoResultsMessage>
              )}
            </>
          )}
        </DropdownContainer>
      )}
    </SelectorContainer>
  );
};

export default FamilySelector;